// No unused vars disabled here for override matching
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  AvailableSeatsDetails,
  EventDateSeatingInfo,
  EventDateSeatingRestrictionInfo,
  EventPriceDto,
  SeatMapAnnotationDto,
  SeatMapDto,
  SeatMapSeatDto,
  SeatMapSectionDto,
  SectionTypes,
  TicketPurchaseTicket,
} from "@/models.g";
import {
  SeatMapSeatDto as $seatMeta,
  SeatMapAnnotationDto as $annotationMeta,
} from "@/metadata.g";
import {
  PublicPurchaseServiceViewModel,
  TicketPurchaseDtoViewModel,
} from "@/viewmodels.g";
import { cartQuantityChanged } from "@common/analytics";
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { watch, reactive } from "vue";
import { GeneralAdmissionSeatNumber } from "../app-private/consts";
import { eventBus, EventTypes } from "../eventBus";

const SEAT_MAP_ZOOM_SESSION_STORAGE_KEY = "seatMap.zoom";
const SEAT_MAP_CURRENT_SCROLL_SESSION_STORAGE_KEY = "seatMap.currentScroll";

export type Drawable = SeatMapSeatDto | SeatMapAnnotationDto;

export function isSeat(object: Drawable | null): object is SeatMapSeatDto {
  return object?.$metadata == $seatMeta;
}

export function isAnnotation(
  object: Drawable | null,
): object is SeatMapAnnotationDto {
  return object?.$metadata == $annotationMeta;
}

export class SeatMapContext {
  protected staticData = useStaticData();

  /** Whether hidden sections should be visible in this context.
   * True in editor/admin contexts, false in public purchase contexts. */
  readonly showHiddenSections: boolean = false;

  constructor(
    public seatMap: SeatMapDto,
    public nextTick: (x: () => void) => void,
  ) {
    const mouseUp = (event: MouseEvent) => this.onGlobalMouseUp(event);
    document.addEventListener("mouseup", mouseUp);

    const mouseMove = (event: MouseEvent) => this._onGlobalMouseMove(event);
    document.addEventListener("mousemove", mouseMove);

    const keyDown = (event: KeyboardEvent) => this.onGlobalKeyDown(event);
    document.addEventListener("keydown", keyDown, { capture: true });

    const keyUp = (event: KeyboardEvent) => this.onGlobalKeyUp(event);
    document.addEventListener("keyup", keyUp, { capture: true });

    this._destroyFunctions.push(() => {
      document.removeEventListener("mouseup", mouseUp);
      document.removeEventListener("mousemove", mouseMove);
      document.removeEventListener("keydown", keyDown);
      document.removeEventListener("keyup", keyUp);
    });
  }

  protected _destroyFunctions: (() => void)[] = [];

  public onDestroy = () => this._destroyFunctions.forEach((x) => x());

  /** Size in pixels of a seat icon */
  readonly seatSize = 30;

  /** Amount of extra empty space to render on the right and bottom edges. */
  bottomRightBuffer = 500;

  private initialZoom = 1;
  public get zoom() {
    const saved = sessionStorage.getItem(SEAT_MAP_ZOOM_SESSION_STORAGE_KEY);

    if (saved) {
      return parseFloat(saved);
    } else {
      this.zoom = this.initialZoom;
      return this.initialZoom;
    }
  }

  public set zoom(value) {
    const actualZoom = +Math.min(
      this.zoomMax,
      Math.max(this.zoomMin, value),
    ).toFixed(4);
    sessionStorage.setItem(
      SEAT_MAP_ZOOM_SESSION_STORAGE_KEY,
      actualZoom.toString(),
    );
  }

  zoomMin = 0.01;
  zoomMax = 3;

  get hideSeatText() {
    return this.zoom < 0.6;
  }

  showGrid = false;

  /** True if a drag has been processed in the current mouseDown action. */
  hasDragged = false;

  currentPinchZoom: null | {
    startDist: number;
    startZoom: number;
    oldXCoord: number;
    oldYCoord: number;
    scrollContainer: HTMLElement;
  } = null;

  currentPanAction: {
    /** The X coordinate of the origin. */
    x: number;
    /** The Y coordinate of the origin. */
    y: number;
    el: HTMLElement;
    distance: number;
  } | null = null;

  private _selectedObjects = reactive(new Set<Drawable>());
  private _selectedObject: Drawable | null = null;

  public get selectedObjects() {
    return this._selectedObjects;
  }

  // Add a getter for the size
  public get selectedObjectsSize() {
    return computed(() => this._selectedObjects.size);
  }

  // Update selectedObjects setter to trigger reactivity.
  public set selectedObjects(value: Set<Drawable>) {
    this._selectedObjects.clear();
    value.forEach((item) => this._selectedObjects.add(item));
    this._selectedObject =
      value.size == 1 ? value.values().next().value! : null;
  }

  public get selectedObject() {
    return this._selectedObject;
  }

  get drawingTransform() {
    return "scale(" + this.zoom + ") ";
  }

  get allSeats() {
    return this.seatMap.sections!.reduce(
      (p, c) => [...p, ...c.seats!],
      <SeatMapSeatDto[]>[],
    );
  }

  /** Details about scroll position used when handling mouse moves. */
  pointerEventOrigin: {
    /** An element that should be watched for scroll changes during drag events so that the position deltas can compensate for scroll changes. */
    scrollEl: HTMLElement;
    scrollX: number;
    scrollY: number;
  } | null = null;

  /** The selected seat if exactly one object is selected and that object is a seat. Otherwise null. */
  get selectedSeat() {
    return isSeat(this.selectedObject) ? this.selectedObject : null;
  }

  get selectedSeats() {
    return [...this.selectedObjects].filter(isSeat);
  }

  /** The selected annotation if exactly one object is selected and that object is a annotation. Otherwise null. */
  get selectedAnnotation() {
    return isAnnotation(this.selectedObject) ? this.selectedObject : null;
  }

  startPointerEvent(event: MouseEvent) {
    const scrollEl = this.getScrollFrame(event);
    this.pointerEventOrigin = {
      scrollEl,
      scrollX: scrollEl.scrollLeft,
      scrollY: scrollEl.scrollTop,
    };
  }

  onDrawingMouseDown(event: MouseEvent) {
    if (event.button != 0) return;
    this.panBegin(event);
  }

  _lastMouseMove?: MouseEvent;

  _onGlobalMouseMove(event: MouseEvent) {
    let deltaX = 0;
    let deltaY = 0;

    if (this._lastMouseMove) {
      // event.movementX and event.movementY cannot be used,
      // as they do not properly factor in the browser's zoom level.
      // `window.devicePixelRatio` cannot be used for this because it
      // also factors in OS display scaling, which IS already factored into
      // the `.movement*` event props.
      deltaX = event.clientX - this._lastMouseMove.clientX;
      deltaY = event.clientY - this._lastMouseMove.clientY;
    }

    this._lastMouseMove = event;

    if (this.pointerEventOrigin) {
      // Check if the scroll frame has shifted position since the last update,
      // e.g. via auto scrolling when near the edge (firefox default behavior),
      // or by a simultaneous middle-click-and-drag.
      // Add any scroll deltas to the overall movement delta.
      const origin = this.pointerEventOrigin;
      const { scrollLeft, scrollTop } = origin.scrollEl;

      deltaX -= origin.scrollX - scrollLeft;
      origin.scrollX = scrollLeft;

      deltaY -= origin.scrollY - scrollTop;
      origin.scrollY = scrollTop;
    }

    this.onGlobalMouseMove(event, deltaX, deltaY);
  }

  onGlobalMouseMove(event: MouseEvent, deltaX: number, deltaY: number) {
    this.panMove(event, deltaX, deltaY);
  }

  onGlobalMouseUp(event: MouseEvent) {
    this.panEnd();
  }

  onObjectClick(event: MouseEvent, object: Drawable) {}

  onObjectMouseDown(event: MouseEvent, object: Drawable) {}

  onGlobalKeyDown(event: KeyboardEvent) {}

  onGlobalKeyUp(event: KeyboardEvent) {}

  getSeatClasses(
    seat: SeatMapSeatDto,
    section: SeatMapSectionDto,
  ): { classes: Object; agentNote?: string; purchaserName?: string } {
    return {
      classes: {
        active: this.selectedObjects.has(seat),
        gaa: section.type === SectionTypes.GeneralAdmission,
      },
    };
  }

  onTouchStart(e: TouchEvent) {
    if (e.touches.length === 2) {
      // Initiate Pinch-to-zoom
      const scrollContainer = e.currentTarget as HTMLElement;

      const xMidpoint = (e.touches[0].pageX + e.touches[1].pageX) / 2;
      const yMidpoint = (e.touches[0].pageY + e.touches[1].pageY) / 2;
      const oldZoom = this.zoom;

      this.currentPinchZoom = {
        startDist: Math.hypot(
          e.touches[0].pageX - e.touches[1].pageX,
          e.touches[0].pageY - e.touches[1].pageY,
        ),
        // old*Coord is the diagram-relative (comparable to seat/annotation x/y values)
        // position of the midpoint of the pinch gesture.
        oldXCoord:
          (scrollContainer.scrollLeft +
            xMidpoint -
            scrollContainer.getBoundingClientRect().left) /
          oldZoom,
        oldYCoord:
          (scrollContainer.scrollTop +
            yMidpoint -
            (scrollContainer.getBoundingClientRect().top + window.scrollY)) /
          oldZoom,
        startZoom: oldZoom,
        scrollContainer: scrollContainer,
      };
      e.preventDefault();
      e.stopPropagation();
    }
  }

  onTouchMove(e: TouchEvent) {
    if (this.currentPinchZoom) {
      // Handle pinch-to-zoom adjustment
      e.preventDefault();
      e.stopPropagation();
      const dist = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY,
      );
      const xMidpoint = (e.touches[0].pageX + e.touches[1].pageX) / 2;
      const yMidpoint = (e.touches[0].pageY + e.touches[1].pageY) / 2;

      const oldZoom = this.zoom;
      this.zoom =
        this.currentPinchZoom.startZoom *
        (dist / this.currentPinchZoom.startDist);

      this.adjustScrollAfterZoom(
        this.currentPinchZoom.scrollContainer,
        xMidpoint,
        yMidpoint,
        oldZoom,
      );
    }
  }

  onTouchEnd(event: TouchEvent) {
    // Terminate pinch-to-zoom
    this.currentPinchZoom = null;
  }

  onMouseWheel(event: WheelEvent) {
    if (event.ctrlKey) {
      // Handle ctrl+mousewheel zooms.
      event.stopPropagation();
      event.preventDefault();

      const scrollContainer = event.currentTarget as HTMLElement;
      const oldZoom = this.zoom;

      // This weird math makes it so that a zoom in followed by a zoom out
      // of the same deltaY magnitude returns us to the exact original zoom.
      // Essentially you can think of `this.zoom` as the output
      // of a function `f(x) = base^x`.
      // We take the `log_base` of our zoom to give us the `x` value,
      // then we take a step along the `x` axis scaled in proportion to the wheel movement,
      // then we reapply the exponent.
      // This is exponential because the more zoomed in you are, the bigger the changes in
      // zoom need to be. When you're at 6x zoom, doubling that is 12x zoom.

      // `base` and `xStepMult` chosen through trial and error.
      // A base of 2 and a mult of 1 would double/half the zoom
      // for each typical scrollwheel click, for example.
      const base = 2;
      const xStepMult = 0.5;
      this.zoom = Math.pow(
        base,
        Math.log(this.zoom) / Math.log(base) -
          (xStepMult * event.deltaY) /
            100 /* Typical zoom step of a stepped (non-smooth) mousewheel is 100)*/,
      );

      this.adjustScrollAfterZoom(
        scrollContainer,
        event.pageX,
        event.pageY,
        oldZoom,
      );
    }
  }

  onDoubleClick(event: MouseEvent) {
    if (event.button != 0) return;
    event.preventDefault();
    event.stopPropagation();

    const scrollContainer = event.currentTarget as HTMLElement;
    const oldZoom = this.zoom;
    this.zoom *= 2;

    this.adjustScrollAfterZoom(
      scrollContainer,
      event.pageX,
      event.pageY,
      oldZoom,
    );
  }

  /** Adjust the scrolling of the target element so that the content located
   * at the provided page-relative coordinates remains at the same location on screen
   * after the zoom of the scroll container was adjusted. */
  private adjustScrollAfterZoom(
    scrollContainer: HTMLElement,
    pageX: number,
    pageY: number,
    oldZoom: number,
  ) {
    const newZoom = this.zoom;

    if (oldZoom == newZoom) {
      // If the zoom was clamped by min/max, don't adjust the scroll position.
      return;
    }

    const { x: oldXCoord, y: oldYCoord } = getRelativeCoordinates(
      pageX,
      pageY,
      scrollContainer,
    );

    const newXCoord = (oldXCoord / oldZoom) * newZoom;
    const newYCoord = (oldYCoord / oldZoom) * newZoom;

    // Wait for vue to render the new zoom into the DOM.
    // If we don't wait, and we're zooming in, we might not be able
    // to scroll as far as we need to since the scroll element's
    // content area won't have its larger size yet.
    const oldLeft = scrollContainer.scrollLeft;
    const oldTop = scrollContainer.scrollTop;
    this.nextTick(() => {
      // scrollLeft/scrollTop normally *truncate* to an integer
      // when they're provided with a non-integar number.
      // However, we want to *round* to the nearest integer.
      // Otherwise, when pinch-zooming, the map will slowly slide towards the bottom right
      // (i.e. scrolling up and left), even when fingers are held stationary.
      scrollContainer.scrollLeft = Math.round(oldLeft + newXCoord - oldXCoord);
      scrollContainer.scrollTop = Math.round(oldTop + newYCoord - oldYCoord);
    });
  }

  zoomButton(event: MouseEvent, mult: number) {
    const oldZoom = this.zoom;
    this.zoom *= mult;

    const scrollFrame = this.getScrollFrame(event);

    const boundingRect = scrollFrame.getBoundingClientRect();
    this.adjustScrollAfterZoom(
      scrollFrame,
      boundingRect.left + scrollFrame.clientWidth / 2 + window.scrollX,
      boundingRect.top + scrollFrame.clientHeight / 2 + window.scrollY,
      oldZoom,
    );
  }

  zoomOutButton(event: MouseEvent) {
    this.zoom /= 2;
  }

  panBegin(event: MouseEvent) {
    const scrollFrame = this.getScrollFrame(event);

    this.currentPanAction = {
      x: event.pageX,
      y: event.pageY,
      el: scrollFrame,
      distance: 0,
    };
    event.preventDefault();
  }

  panMove(event: MouseEvent, deltaX?: number, deltaY?: number) {
    if (this.currentPanAction) {
      // We will use provided deltas if available (from _onGlobalMouseMove), otherwise fall back to movementX/Y
      const moveX = deltaX !== undefined ? deltaX : event.movementX;
      const moveY = deltaY !== undefined ? deltaY : event.movementY;

      event.preventDefault();

      this.currentPanAction.distance = Math.sqrt(
        Math.pow(this.currentPanAction.x - event.pageX, 2) +
          Math.pow(this.currentPanAction.y - event.pageY, 2),
      );

      // Apply movement to scroll - when deltas are provided from _onGlobalMouseMove,
      // they are already compensated for zoom and scroll changes
      this.currentPanAction.el.scrollLeft -= moveX;
      this.currentPanAction.el.scrollTop -= moveY;

      if ((this.currentPanAction?.distance ?? 0) > 30) {
        // If the user was panning, don't handle as a click if they went beyond some amount.
        this.hasDragged = true;
      }

      return true;
    }
    return false;
  }

  panEnd() {
    const wasPanning = !!this.currentPanAction;
    this.saveCurrentScroll();
    this.currentPanAction = null;
    if (this.hasDragged) {
      setTimeout(() => (this.hasDragged = false), 100);
    }
    return wasPanning;
  }

  getSectionForSeat(seat: SeatMapSeatDto) {
    return this.seatMap.sections?.find((s) => s.seats?.includes(seat)) ?? null;
  }

  /**
   * This function should be called when loading the seat map for the first time (when the component mounted)
   */
  initialPan() {
    const scrollFrame = this.getScrollFrame();
    if (!scrollFrame) {
      return;
    }

    const currentScroll = this.getCurrentScroll();
    scrollFrame.scrollLeft = currentScroll.left;
    scrollFrame.scrollTop = currentScroll.top;
  }

  private getScrollFrame(event: MouseEvent | null = null) {
    if (event) {
      const scrollFrame = (event.target as HTMLElement).closest<HTMLElement>(
        ".seat-map-scroll-frame",
      )!;

      if (scrollFrame) {
        return scrollFrame;
      }
    }

    // either no event was passed or the scrollframe wasn't found via the logic above;
    // in this case, look for it from the document root
    return document.getElementsByClassName(
      "seat-map-scroll-frame",
    )[0] as HTMLElement;
  }

  private saveCurrentScroll() {
    const currentScroll = {
      left: this.currentPanAction?.el?.scrollLeft || 0,
      top: this.currentPanAction?.el?.scrollTop || 0,
    };
    sessionStorage.setItem(
      SEAT_MAP_CURRENT_SCROLL_SESSION_STORAGE_KEY,
      JSON.stringify(currentScroll),
    );
  }

  private getCurrentScroll() {
    const currentScroll = sessionStorage.getItem(
      SEAT_MAP_CURRENT_SCROLL_SESSION_STORAGE_KEY,
    );
    if (currentScroll) {
      return JSON.parse(currentScroll);
    }

    return { left: 0, top: 0 };
  }
}

/**
 * Given a pair of page-relative coordinates,
 * compute the coordinates relative to the top left corner of `referenceElement`.
 * Modified from https://stackoverflow.com/a/36860652.
 */
function getRelativeCoordinates(
  pageX: number,
  pageY: number,
  referenceElement: HTMLElement | null,
) {
  const offset = {
    left: 0,
    top: 0,
  };

  let reference = referenceElement;

  while (reference) {
    offset.left +=
      reference.offsetLeft -
      reference.scrollLeft +
      parseInt(getComputedStyle(reference).paddingLeft);
    offset.top +=
      reference.offsetTop -
      reference.scrollTop +
      parseInt(getComputedStyle(reference).paddingTop);
    reference = reference.offsetParent as HTMLElement | null;
  }

  return {
    x: pageX - offset.left,
    y: pageY - offset.top,
  };
}

export class EventPreviewSeatMapContext extends SeatMapContext {
  override readonly showHiddenSections = true;

  constructor(
    seatMap: SeatMapDto,
    protected setPriceSelect: (value: {
      seat: SeatMapSeatDto;
      section: SeatMapSectionDto;
    }) => void,
    nextTick: (x: () => void) => void,
  ) {
    super(seatMap, nextTick);
  }

  override getSeatClasses(seat: SeatMapSeatDto, section: SeatMapSectionDto) {
    return {
      classes: {
        active: true,
      },
    };
  }

  override onObjectClick(event: MouseEvent, obj: Drawable) {
    if (this.hasDragged || !isSeat(obj)) {
      return;
    }

    const section = this.getSectionForSeat(obj)!;
    this.setPriceSelect({ seat: obj, section: section });
  }
}

export class PurchaseSeatMapContext extends SeatMapContext {
  originalPurchase: TicketPurchaseDtoViewModel | null = null;
  seatsBeingExchanged: Set<string> = new Set();
  _highlightedPriceId: number | null = null;
  _highlightedSectionName: string | null = null;

  _isInSelectMode: boolean = false;
  _cachedAvailableSeats: Set<string> | null = null;
  _cachedRestrictedSeats: Set<string> | null = null;
  _purchaserNamesBySeat: Map<string, string> | null = null;

  constructor(
    private info: EventDateSeatingInfo,
    protected purchase: TicketPurchaseDtoViewModel,
    protected service: PublicPurchaseServiceViewModel,
    protected setPriceSelect: (value: {
      seat: SeatMapSeatDto;
      section: SeatMapSectionDto;
      restrictions?: EventDateSeatingRestrictionInfo[];
      availableAmount: number;
    }) => void,
    protected setTakenSeatInfoSelect: (value: { seat: string | null }) => void,
    nextTick: (x: () => void) => void,
  ) {
    super(info.seatMap!, nextTick);

    // Build seat → purchaser name lookup for agent/admin tooltip display.
    // This property is only present for agent/admin users (role-restricted on the server),
    // and doesn't exist in the public app's TypeScript types.
    const takenNames = (info as any).takenSeatPurchaserNames as
      | Array<{ seatNumber: string; purchaserName: string }>
      | undefined;
    if (takenNames?.length) {
      this._purchaserNamesBySeat = new Map(
        takenNames.map((n) => [n.seatNumber, n.purchaserName]),
      );
    }

    if (this.purchase.originalPurchase) {
      this.originalPurchase = new TicketPurchaseDtoViewModel();
      this.originalPurchase
        .$load(this.purchase.originalPurchase!.publicId!)
        .then(() => {
          this.seatsBeingExchanged = new Set();
          if (
            this.originalPurchase?.eventDateId == this.purchase.eventDateId!
          ) {
            // The tickets from the original purchase that are being exchanged.
            // These seats are also available for selection
            // (e.g. exchanging an adult ticket for a child ticket, for the same seat)
            const pendingExchangeTickets =
              this.originalPurchase.tickets?.filter((t) =>
                this.purchase.originalPurchase?.ticketIds?.includes(
                  t.ticketId!,
                ),
              )!;

            for (const pendingExchangeTicket of pendingExchangeTickets) {
              if (pendingExchangeTicket.seatNumber)
                this.seatsBeingExchanged.add(pendingExchangeTicket.seatNumber);
            }
          }
        });
    }

    /**============================================
     This is a regular seat map purchase context
     ============================================*/
    // Check if there were seat selected from the previous instance
    this.selectedObjects = new Set(
      this.allSeats.filter((seat) =>
        this.purchase.tickets?.some((t) => t.seatNumber === seat.seatNumber),
      ),
    );

    // Watch for changes in info.availableSeats and info.restrictions
    // to invalidate available seats cache and notify UI to re-render
    watch(
      [() => this.info.availableSeats, () => this.info.restrictions],
      () => {
        this._cachedAvailableSeats = null; // invalidate the cache
        eventBus.emit(EventTypes.RefreshSeatMap);
      },
      { deep: true },
    );

    // Watch for changes in info.restrictions
    // to invalidate restricted seat cache and notify UI to re-render
    watch(
      () => this.info.restrictions,
      () => {
        this._cachedRestrictedSeats = null; // invalidate the cache
        eventBus.emit(EventTypes.RefreshSeatMap);
      },
      { deep: true },
    );

    this._destroyFunctions.push(() => this.stopListening());
    this.startListening();
  }

  get highlightedSectionName(): string | null {
    return this._highlightedSectionName;
  }
  set highlightedSectionName(sectionName: string | null) {
    this._highlightedSectionName = sectionName;
    this.nextTick(() => {
      this.selectedObjects = new Set(this.selectedObjects);
    });
  }

  get highlightedPriceId(): number | null {
    return this._highlightedPriceId;
  }

  set highlightedPriceId(priceId: number | null) {
    this._highlightedPriceId = priceId;
    //todo: IS THIS NEEDED?
    this.nextTick(() => {
      // Force reactivity update by touching the selectedObjects
      // This will cause components watching the context to re-render
      this.selectedObjects = new Set(this.selectedObjects);
    });
  }
  get isInSelectMode(): boolean {
    return this._isInSelectMode;
  }
  set isInSelectMode(value: boolean) {
    this._isInSelectMode = value;
    if (!value) {
      // Clear highlighting when exiting select mode
      this._highlightedPriceId = null;
    }
  }

  get cachedAvailableSeats(): Set<string> {
    if (!this._cachedAvailableSeats) {
      this._cachedAvailableSeats = new Set([
        ...this.info.availableSeats!.filter(
          (seat) => !this.cachedRestrictedSeats.has(seat),
        ),
        ...[...this.seatsBeingExchanged].filter(
          (seat) => !this.cachedRestrictedSeats.has(seat),
        ),
      ]);
    }
    return this._cachedAvailableSeats;
  }

  private get cachedRestrictedSeats(): Set<string> {
    if (!this._cachedRestrictedSeats) {
      this._cachedRestrictedSeats = new Set();
      const purchase = this.purchase;
      for (const restriction of this.info.restrictions || []) {
        // Per #813 - agent-only seats for public purchases look exactly like sold seats.
        if (!restriction.agentOnly || purchase.isPublic) {
          for (const seat of restriction.seats ?? []) {
            this._cachedRestrictedSeats.add(seat);
          }
        }
      }
    }
    return this._cachedRestrictedSeats;
  }

  get availableSeats() {
    return this.cachedAvailableSeats;
  }

  override getSeatClasses(seat: SeatMapSeatDto, section: SeatMapSectionDto) {
    const availableSeats = this.availableSeats;
    let agentNote = undefined;
    let isReservedForAgent = false;
    let isReserved = false;

    // Updating agentNote
    for (const restriction of this.info.restrictions || []) {
      if (restriction.seats?.includes(seat.seatNumber!)) {
        if (restriction.agentOnly) {
          isReservedForAgent = true;
        } else isReserved = true;
        agentNote = restriction.agentNote ?? seat.seatNumber!;
      }
    }

    // Check if seat should be blocked due to select mode
    let isBlockedBySelectMode = false;
    if (this._isInSelectMode && this._highlightedPriceId) {
      const seatHasHighlightedPrice = section.prices?.some(
        (p) => p.id === this._highlightedPriceId,
      );
      // Only allow selection if section matches
      const sectionMatches =
        !this._highlightedSectionName ||
        section.name === this._highlightedSectionName;
      isBlockedBySelectMode = !(seatHasHighlightedPrice && sectionMatches);
    }

    let classes: Object = {};

    if (
      this.selectedObjects.has(seat) &&
      section.type != SectionTypes.GeneralAdmission &&
      !isBlockedBySelectMode
    ) {
      classes = {
        ...classes,
        checked: true,
      };
    } else {
      const seatIsAvailable = availableSeats.has(seat.seatNumber!);
      classes = {
        ...classes,
        taken:
          !isBlockedBySelectMode &&
          ((!seatIsAvailable &&
            (!this.staticData.canAccessAdminArea ||
              !this.staticData.canAccessAgentArea)) ||
            isReserved ||
            section.isHidden),
        blocked: isBlockedBySelectMode,
        "taken-with-info":
          !seatIsAvailable &&
          (this.staticData.canAccessAdminArea ||
            this.staticData.canAccessAgentArea) &&
          !isReserved &&
          !isBlockedBySelectMode &&
          !section.isHidden,
        active:
          seatIsAvailable &&
          !isReserved &&
          !isBlockedBySelectMode &&
          !section.isHidden,
        "original-seat": this.seatsBeingExchanged.has(seat.seatNumber!),
        "price-highlighted":
          this._highlightedPriceId &&
          section.prices?.some((p) => p.id === this._highlightedPriceId) &&
          seatIsAvailable &&
          !isBlockedBySelectMode &&
          (!this._highlightedSectionName ||
            section.name === this._highlightedSectionName),
      };
    }

    const purchaserName =
      this._purchaserNamesBySeat?.get(seat.seatNumber!) ?? undefined;
    return { classes, agentNote, purchaserName };
  }

  async removeReservedSeatTicketFromCart(
    ticket: TicketPurchaseTicket,
  ): Promise<void> {
    if (!ticket.ticketId || !ticket.seatNumber) return;

    return this.service
      .removeTicket(this.purchase.publicId, ticket.ticketId)
      .then(() => {
        // Update available seats
        this.availableSeats.add(ticket.seatNumber!);

        // Update visual state - find and deselect the seat
        const seatObj = this.allSeats.find(
          (s) => s.seatNumber === ticket.seatNumber,
        );
        if (seatObj) {
          this.selectedObjects.delete(seatObj);
        }

        // Send analytics events
        const price = this.purchase.eventDate?.eventPrices?.find(
          (p) => p.id == ticket.eventPriceId,
        );
        if (price) {
          cartQuantityChanged(this.purchase, price, -1);
        }
      });
  }

  getTicketsInCartForPrice = (eventPrice: EventPriceDto) => {
    const tickets = this.purchase.tickets || [];
    return tickets.filter((ticketPurchase) => {
      return ticketPurchase.eventPriceId == eventPrice.id;
    });
  };

  async addTicket(seat: SeatMapSeatDto, eventPrice: EventPriceDto) {
    if (this.cannotAddMoreTickets(eventPrice)) {
      alert(
        "You've already selected the maximum amount of tickets for this event",
      );
      return;
    }
    try {
      // Preemptively add the seat to the set of objects
      // so that it doesn't briefly appear as "taken"
      // when the SignalR realtime availability update
      // comes before the response to addTicket.
      this.selectedObjects.add(seat);
      this.service.addTicket.args = {
        publicId: this.purchase.publicId,
        eventPriceId: eventPrice.id,
        seat: seat.seatNumber,
      };
      // This is invoked with args for easy tracking of which event price was clicked,
      // so we can show appropriate loading indicators in the UI.
      await this.service.addTicket.invokeWithArgs();

      // Send analytics events:
      cartQuantityChanged(this.purchase, eventPrice, 1);
    } catch {
      this.selectedObjects.delete(seat);
    }
  }

  getAvailableTicketCount = (eventPrice: EventPriceDto) => {
    const currentTicketsInCartCount =
      this.getTicketsInCartForPrice(eventPrice).length;
    const maximumTicketsCanPurchase = eventPrice.maximumPurchaseQuantity
      ? eventPrice.maximumPurchaseQuantity
      : 9999;
    return Math.max(maximumTicketsCanPurchase - currentTicketsInCartCount, 0);
  };

  cannotAddMoreTickets = (eventPrice: EventPriceDto) => {
    const availableTicketCount = this.getAvailableTicketCount(eventPrice);
    return availableTicketCount == 0;
  };

  handleSelectedSeatClick(obj: SeatMapSeatDto) {
    const ticket = this.purchase.tickets?.find(
      (t) => t.seatNumber == obj.seatNumber,
    );
    if (!ticket) return;

    if (ticket.isReservedIndividualSeat) {
      this.removeReservedSeatTicketFromCart(ticket);
      return;
    }
  }

  // Click on a seat
  override onObjectClick(event: MouseEvent, obj: Drawable) {
    if (this.hasDragged || !isSeat(obj)) {
      return;
    }

    const section = this.getSectionForSeat(obj)!;

    // Do not allow selection of seats in hidden sections via the standard UI.
    // These sections are only meant for API integrations.
    if (section?.isHidden) {
      return;
    }

    if (!this.isInSelectMode && section) {
      eventBus.emit(EventTypes.UnselectedSeatMapSectionClicked, section);
    }

    // we should not allow purchase on a different section than the highlighted section
    if (
      this._highlightedSectionName &&
      section.name !== this._highlightedSectionName
    ) {
      return;
    }

    // In select mode for reserved seating
    if (this._isInSelectMode && this._highlightedPriceId) {
      if (this.selectedObjects.has(obj)) {
        this.handleSelectedSeatClick(obj);
        return;
      }
      const eventPrice = section.prices?.find(
        (p) => p.id === this._highlightedPriceId,
      );
      const seatHasHighlightedPrice = !!eventPrice;
      if (!seatHasHighlightedPrice) {
        // Blocked or cannot add more tickets, do nothing
        return;
      }
      this.addTicket(obj, eventPrice);

      return;
    }

    if (this.selectedObjects.has(obj)) {
      const ticket = this.purchase.tickets?.find(
        (t) => t.seatNumber == obj.seatNumber,
      );
      if (!ticket) return;

      if (ticket.isReservedIndividualSeat) {
        this.removeReservedSeatTicketFromCart(ticket);
        return;
      }

      this.handleSelectedSeatClick(obj);
    }
    // neglect any action for public users
    else if (
      !this.availableSeats.has(obj.seatNumber!) &&
      this.staticData.isPublicUser
    ) {
      return;
    }
    // this statement is for agent who can see taken seat info
    else if (
      !this.availableSeats.has(obj.seatNumber!) &&
      !this.staticData.isPublicUser
    ) {
      // Load specific seat info into the bottom sheet (different then price select)
      this.setTakenSeatInfoSelect({ seat: obj.seatNumber });

      return;
    }

    // Load the seat into the bottom sheet popup.
    this.setPriceSelect({
      seat: obj,
      section: section,
      restrictions: this.info.restrictions?.filter((r) =>
        r.seats?.includes(obj.seatNumber!),
      ),
      availableAmount:
        this.info.availableSeatsDetails?.filter(
          (asd) => asd.seatNumber === obj.seatNumber,
        )[0].amountOfSeats || 0,
    });

    // Clear service error state that may be lingering from a previous attempt to pick a seat:
    this.service.addTicket.wasSuccessful = null;
    this.service.addTicket.message = null;
    this.service.addMultipleTickets.wasSuccessful = null;
    this.service.addMultipleTickets.message = null;
    this.service.removeTicket.wasSuccessful = null;
    this.service.removeTicket.message = null;
  }

  _connection: HubConnection | null = null;

  private async startListening() {
    if (this._connection != null) return;

    this._connection = new HubConnectionBuilder()
      .withUrl("/hubs/seating", {
        // Configured to avoid issues with load balancing:
        // https://docs.microsoft.com/en-us/aspnet/core/signalr/scale?view=aspnetcore-6.0#sticky-sessions
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    await this._connection.start();
    await this._connection.send(
      "SubscribeToSeating",
      this.purchase.eventDateId,
    );
    this._connection.on(
      "SeatAvailabilityChanged",
      (data: {
        eventDateId: number;
        availableSeats: string[];
        availableSeatDetail: AvailableSeatsDetails[];
      }) => {
        if (data.eventDateId == this.info.eventDateId) {
          this.info.availableSeats = data.availableSeats;
          this.info.availableSeatsDetails = data.availableSeatDetail;
        }
      },
    );
  }

  private stopListening() {
    this._connection?.stop();
    this._connection = null;
  }
}
